import { useMemo } from 'react';

import { useApplicationGroups } from '../../../hooks/useApplicationGroups';
import useEffectiveApplicationGroupId from '../../../hooks/useEffectiveApplicationGroupId';
import { useGetApplicationGroupOverviewQuery } from '../../../services/switch-rest-api/dashboard.service';
import { isAllSelection } from '../../../utils/selection';

export interface MetricBreakdown {
  total: number;
  online: number;
  offline: number;
  onlinePercentage: number;
}

export interface MeterPointBreakdown {
  total: number;
  bulk: number;
  nonBulk: number;
}

export interface AggregatedMetrics {
  totalApplications: number;
  meters: MetricBreakdown;
  gateways: MetricBreakdown;
  meterPoints: MeterPointBreakdown;
  activeAlerts: number;
  assignedUsers: number;
}

export interface UseAllGroupsMetricsOptions {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

/**
 * Custom hook to fetch and aggregate metrics across all application groups.
 *
 * This hook provides a single source of data for all dashboard widgets when
 * "All Groups" is selected. It leverages RTK Query's automatic caching to
 * ensure only one API request is made regardless of how many widgets use it.
 *
 * Features:
 * - Automatic request deduplication (single API call shared across all widgets)
 * - Client-side aggregation with memoization for performance
 * - Synchronized loading states across all consumers
 * - Only runs when "All Groups" is selected
 *
 * @example
 * ```tsx
 * const { metrics, isLoading, isFetching, isAllGroupsSelected } = useAllGroupsMetrics({
 *   sortBy: 'name',
 *   sortDirection: 'asc'
 * });
 *
 * if (!isAllGroupsSelected) {
 *   // Use individual widget queries
 *   return <SpecificGroupView />;
 * }
 *
 * if (isLoading) {
 *   return <CircularProgress />;
 * }
 *
 * return <div>Total Applications: {metrics?.totalApplications}</div>;
 * ```
 *
 * @param options - Optional sorting parameters
 * @returns Object containing aggregated metrics, loading states, and selection status
 */
export const useAllGroupsMetrics = (options?: UseAllGroupsMetricsOptions) => {
  const appGroupId = useEffectiveApplicationGroupId();
  const isAllGroupsSelected = isAllSelection(appGroupId);
  const { applicationGroups } = useApplicationGroups();
  const canUseAllGroups = useMemo(
    () =>
      applicationGroups.filter(
        (group) => !isAllSelection(group._id) && group.name !== 'All Groups'
      ).length > 1,
    [applicationGroups]
  );

  // Destructure options to avoid referential equality issues in useMemo
  const { sortBy, sortDirection = 'asc' } = options || {};

  // Query with empty params = all groups
  // RTK Query will cache this and share it across all components using this hook
  const {
    data: overviewData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetApplicationGroupOverviewQuery(
    {},
    {
      skip: !isAllGroupsSelected || !canUseAllGroups,
    }
  );

  // Helper to get value from object by path (supports nested like 'meters.total')
  const getValueByPath = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Apply client-side sorting to the fetched data
  const sortedData = useMemo(() => {
    if (!overviewData?.data) return null;
    if (!sortBy) return overviewData;

    const sorted = [...overviewData.data].sort((a, b) => {
      const aValue = getValueByPath(a, sortBy);
      const bValue = getValueByPath(b, sortBy);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      // Compare strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Compare numbers
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return { ...overviewData, data: sorted };
  }, [overviewData, sortBy, sortDirection]);

  // Aggregate data from all groups
  // This runs only when sortedData changes (memoized)
  const aggregated = useMemo<AggregatedMetrics | null>(() => {
    if (!sortedData?.data || sortedData.data.length === 0) {
      return null;
    }

    // Sum up all metrics from all groups
    const result = sortedData.data.reduce(
      (acc, group) => ({
        totalApplications: acc.totalApplications + group.totalApplications,
        meters: {
          total: acc.meters.total + group.meters.total,
          online: acc.meters.online + group.meters.online,
          offline: acc.meters.offline + group.meters.offline,
          onlinePercentage: 0,
        },
        gateways: {
          total: acc.gateways.total + group.gateways.total,
          online: acc.gateways.online + group.gateways.online,
          offline: acc.gateways.offline + group.gateways.offline,
          onlinePercentage: 0,
        },
        meterPoints: {
          total: acc.meterPoints.total + group.meterPoints.total,
          bulk: acc.meterPoints.bulk + group.meterPoints.bulk,
          nonBulk: acc.meterPoints.nonBulk + group.meterPoints.nonBulk,
        },
        activeAlerts: acc.activeAlerts + group.activeAlerts,
        assignedUsers: acc.assignedUsers + group.assignedUsers,
      }),
      {
        totalApplications: 0,
        meters: { total: 0, online: 0, offline: 0, onlinePercentage: 0 },
        gateways: { total: 0, online: 0, offline: 0, onlinePercentage: 0 },
        meterPoints: { total: 0, bulk: 0, nonBulk: 0 },
        activeAlerts: 0,
        assignedUsers: 0,
      }
    );

    return result;
  }, [sortedData]);

  // Calculate percentages after aggregation
  // This runs only when aggregated data changes (memoized)
  const metrics = useMemo(() => {
    if (!aggregated) return null;

    return {
      ...aggregated,
      meters: {
        ...aggregated.meters,
        onlinePercentage:
          aggregated.meters.total > 0
            ? Math.round(
                (aggregated.meters.online / aggregated.meters.total) * 100
              )
            : 0,
      },
      gateways: {
        ...aggregated.gateways,
        onlinePercentage:
          aggregated.gateways.total > 0
            ? Math.round(
                (aggregated.gateways.online / aggregated.gateways.total) * 100
              )
            : 0,
      },
    };
  }, [aggregated]);

  return {
    /** Aggregated metrics across all groups, or null if loading/error/not selected */
    metrics,
    /** Sorted overview data for all groups (used by table for client-side pagination) */
    data: sortedData,
    isLoading,
    isFetching,
    isError,
    error,
    isAllGroupsSelected: isAllGroupsSelected && canUseAllGroups,
    lastUpdated: overviewData?.lastUpdate,
  };
};
